<script lang="ts">
  import * as Dialog from "$lib/components/ui/dialog/index.js";
  import PricingCard from "$lib/components/cards/pricingCard";
  // import BillingToggle from "$lib/components/toggles/billingToggle.svelte";
  import { authStore } from "$lib/stores/authStore";
  import { userOnboardingStore } from "$lib/stores/userOnboardingStore";
  // import { userUpgradeStore } from "$lib/stores/userUpgradeStore";
  import { SubscriptionAPI } from "$lib/api/subscription";
  // import type { SubscriptionTier } from "$lib/types";
  // import { fatLightningIcon, userAiIcon, userThreeIcon } from "$lib/images/icons";
  import { SUBSCRIPTION_TIER } from "$lib/types";
  import { PLANS } from "$lib/data/pricing";
  import { goto } from "$app/navigation";
  import { routes } from "$lib/routes";
  import SubscriptionUpgradeConfirmation from "./upgradeConfirmationDialog.svelte";
  import { trackUpgradeConversion, trackBeginCheckout } from "$lib/utility/adTracking";
  import { onMount } from "svelte";

  let serverPlans = $state<Array<{ name: string; active: boolean; recurringCredits: number }>>([]);
  let billingPeriod = $state<"monthly" | "annual">("monthly");
  let confirmationDialogOpen = $state<boolean>(false);
  let chosenPlan = $state<string>("");
  let error = $state<string | null>(null);

  let {
    open = $bindable(false),
    title = "Plans and Pricing",
    message = "Upgrade for Additional Credits",
    onOpenChange = (value: boolean): void => {},
  } = $props<{
    onOpenChange?: (value: boolean) => void;
    title?: string;
    message?: string;
    open?: boolean;
  }>();

  onMount(async () => {
    try {
      const results = await SubscriptionAPI.getProductPlans();
      serverPlans = results.map((plan) => ({
        name: plan.name,
        active: plan.active,
        recurringCredits: plan.recurringCredits || 0,
      }));

      handleBillingPeriodChange($authStore?.user?.planLabel?.endsWith("_annual") ? "annual" : "monthly");
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  });

  function handleGetStarted(selectedLabel: string) {
    userOnboardingStore.setSignUpIntent(selectedLabel, window.location.pathname);
    open = false;
    goto(routes.signUp);
  }

  function handleUpgrade(selectedLabel: string) {
    if ($authStore.user?.planLabel) {
      chosenPlan = selectedLabel;
      confirmationDialogOpen = true;
    } else {
      trackBeginCheckout(selectedLabel);
      const referral = Rewardful ? Rewardful.referral : "";
      SubscriptionAPI.checkout(selectedLabel, referral);
    }
    open = false;
  }

  function handleManageBilling() {
    open = false;
    SubscriptionAPI.manage();
  }

  function handleBillingPeriodChange(period: "monthly" | "annual") {
    billingPeriod = period;
  }

  // Derived states
  const userPlan = $derived($authStore.user?.planLabel);
  const isLoggedIn = $derived($authStore.isLoggedIn);
  const isAnnualPlan = $derived(userPlan?.includes("_annual") || false);
  const showBillingToggle = $derived(!isLoggedIn || !isAnnualPlan);

  // Base plans configuration
  let plans = $derived(() => {
    const basePlans = [
      {
        title: "Free",
        plan: SUBSCRIPTION_TIER.FREE,
        benefits: PLANS.FREE.benefits,
        description: PLANS.FREE.description,
        actionable: true,
        buttonClass: "bg-black text-white hover:bg-black hover:text-white",
        subtiers: [],
        serverPlans: [],
        billingPeriod,
      },
      {
        title: "Plus",
        plan: SUBSCRIPTION_TIER.PLUS,
        benefits: PLANS.PLUS.benefits,
        description: PLANS.PLUS.description,
        variant: "highlight",
        buyable: true,
        subtiers: PLANS.PLUS.subtiers.map((tier) => ({
          label: tier.label,
          price: tier.price,
        })),
        serverPlans: serverPlans.filter((p) => p.name.startsWith("plus_tier")),
        onAction: (selectedLabel: string) => handleGetStarted(selectedLabel),
        billingPeriod,
      },
      {
        title: "Team",
        plan: SUBSCRIPTION_TIER.TEAM,
        benefits: PLANS.TEAM.benefits,
        description: PLANS.TEAM.description,
        variant: "dark",
        buyable: true,
        subtiers: PLANS.TEAM.subtiers.map((tier) => ({
          label: tier.label,
          price: tier.price,
        })),
        serverPlans: serverPlans.filter((p) => p.name.startsWith("team_tier")),
        buttonClass: "bg-white text-black hover:bg-white hover:text-black",
        onAction: (selectedLabel: string) => handleGetStarted(selectedLabel),
        billingPeriod,
      },
    ];

    // return isLoggedIn ? basePlans.filter(p => p.title !== "Free") : basePlans;
    return basePlans;
  });

  const pricingPlans = $derived(() => {
    return plans().map((plan: any) => ({
      ...plan,
      billingPeriod,
      isSignInPrompt: !isLoggedIn,
      isUpgradeable: isLoggedIn && plan.title !== "Free",
      onUpgrade: isLoggedIn && plan.title !== "Free" ? (selectedLabel: string) => handleUpgrade(selectedLabel) : null,
      onAction:
        !isLoggedIn && plan.title !== "Free" ? (selectedLabel: string) => handleGetStarted(selectedLabel) : undefined,
      currentPlanLabel: $authStore.user?.planLabel || "",
    }));
  });

  // Reset sign up intent when dialog opens for guests
  $effect(() => {
    if (open && !isLoggedIn) {
      userOnboardingStore.reset();
    }
  });
</script>

<SubscriptionUpgradeConfirmation bind:open={confirmationDialogOpen} bind:toTierLabel={chosenPlan} />

<Dialog.Root bind:open {onOpenChange}>
  <Dialog.Portal>
    <Dialog.Overlay class="fixed inset-0 bg-black/50" />
    <Dialog.Content
      class="no-scrollbar fixed left-1/2 top-1/2 max-h-[90vh] w-full max-w-7xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-theme-background-primary p-9 shadow-lg dark:border-white/20"
    >
      <div class="flex items-center justify-between">
        <Dialog.Title class="text-[32px] font-semibold text-theme-text-primary">{title}</Dialog.Title>
      </div>
      <Dialog.Description class="mb-6 text-[20px] font-light text-theme-text-primary">
        {message}
      </Dialog.Description>

      {#if error}
        <div class="mb-4 text-red-500">
          Error loading plans: {error}
        </div>
      {/if}

      <!-- <div class="mb-6">
        <BillingToggle
          onChange={handleBillingPeriodChange}
          wrapperClass="justify-start"
          disabled={!showBillingToggle}
          defaultValue={isAnnualPlan ? 'annual' : 'monthly'} />
      </div> -->

      <div
        class="isolate mx-auto grid w-full max-w-none auto-rows-auto grid-cols-1 gap-8 md:grid-cols-2 lg:mx-0 xl:grid-cols-3"
      >
        {#each pricingPlans() as plan (plan.plan)}
          <PricingCard {...plan} on:closeDialog={() => (open = false)} on:manageBilling={handleManageBilling} />
        {/each}
      </div>
      {#if $authStore.user && $authStore.user.plan !== "free"}
        <div class="mt-0 flex justify-center">
          <button
            class="text-xs text-theme-text-tertiary underline transition-colors hover:text-theme-text-primary"
            onclick={() => SubscriptionAPI.manage()}
          >
            Manage subscription
          </button>
        </div>
      {/if}
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
